import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ActionButton,
    Content,
    ContextualHelp,
    Flex,
    Heading,
    Item,
    Menu,
    MenuTrigger,
    Section,
    Text,
} from '@adobe/react-spectrum';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Label,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { BaseDeckPane } from './BaseDeckPane';
import { ScoreDriverRow } from './PowerLevelPane';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber } from '../../common/cardStats/formatters';
import { deriveManabaseScoreDrivers } from '../../../data/deckProfileMarkers';
import {
    CHART_ACCENT_GOLD,
    CHART_ACCENT_RED,
    CHART_AXIS_STYLE,
    CHART_GRID_STROKE,
    CHART_GRID_STROKE_DASH,
    CHART_INK,
    CHART_INK_MUTED,
    CHART_LABEL_STYLE,
    CHART_LEGEND_WRAPPER_STYLE,
    CHART_MANA_COLORS,
    SEARED_TOOLTIP_CURSOR,
    searedAreaGradient,
    searedLegendFormatter,
    SearedTooltip,
    gradientId,
} from '../../common/cardStats/chartTheme.jsx';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';
import './manabasePane.css';
import './mana-cost.css';
// Reuses the collapsible-section classes (powerSection, powerSectionToggle,
// powerSectionBody, etc.) and the score-driver grid (powerDrivers*,
// powerDriverRow*) defined by PowerLevelPane.css. Importing here makes the
// dependency explicit so the pane renders correctly even when visited
// without first loading the power-level pane.
import './PowerLevelPane.css';

const CATEGORY_CONSTANTS = {
    ritualScores: { label: 'Rituals' },
    dorkScores: { label: 'Dorks' },
    rockScores: { label: 'Rocks' },
    landScores: { label: 'Lands' },
    treasureScores: { label: 'Treasure' },
    otherScores: { label: 'Other' },
    landRampScores: { label: 'Land Ramp' },
};

// ---- Quality scorecard -----------------------------------------------
// `manabase.percentages` and `manabase.thresholds` ship a clean set of
// four 0–100+ grades the engine actually evaluates against. Surface them
// inside the hero header so users see *why* a deck scored the way it did.
const QUALITY_SCORES = [
    { key: 'overall', label: 'Overall', hint: 'weighted total' },
    { key: 'manaFixing', label: 'Fixing', hint: 'color fixing' },
    { key: 'quality', label: 'Quality', hint: 'source quality' },
    { key: 'curve', label: 'Curve', hint: 'castability' },
];

// Hero headline archetypes — lifted from the backend profile strengths
// so the name describes *what the deck does* (like the salt panel's
// personality) rather than grading it. Priority-ordered so the most
// distinctive trait wins; first id found in `profile.strengths` (which
// the engine already ships in priority order) claims the headline.
const MANABASE_HERO_STRENGTH_NAMES = {
    premiumFetchPackage: 'Fetch Crucible',
    fastManaLandPackage: 'Fast-Mana Base',
    originalDualPackage: 'ABU-Era Base',
    rainbowSourceDense: 'Rainbow Base',
    mdfcPackage: 'MDFC Base',
    commanderAcceleration: 'Commander-Boosted',
    untappedBase: 'Untapped Base',
    utilityLandPackage: 'Utility Base',
    rampAbundant: 'Ramp-Loaded Base',
    fetchDensityHigh: 'Fetch-Heavy Base',
    fixingQualityHigh: 'Premium Fixing',
    pipCoverageHigh: 'Tight Fixing',
    colorSourcesBalanced: 'Balanced Fixing',
    lowCurveMatchesBase: 'Low-Curve Base',
    curveAppealHigh: 'Curve-Smooth Base',
    sourceCountSurplus: 'Oversourced Base',
    highTurnOneColorOdds: 'Turn-1 Ready',
    landCountHealthy: 'Classic Base',
};

// Fallback by color count when no strength trait is dominant — pure
// description, no grading. Keys accept number of distinct colors the
// deck produces; anything outside 1–5 falls through to the default.
const MANABASE_HERO_COLOR_NAMES = {
    1: 'Monocolor Base',
    2: 'Two-Color Base',
    3: 'Three-Color Base',
    4: 'Four-Color Base',
    5: 'Five-Color Base',
};

// Tone still tracks the overall score — drives the left-border color so
// high-scoring decks glow green while low-scoring ones stay neutral. The
// *name* is decoupled from the tone so a cEDH deck with a thin base
// still gets a descriptive identity, never a "Developing Base" scolding.
function manabaseHeroTone(normalizedPct) {
    const n = Number(normalizedPct);
    if (!Number.isFinite(n)) return 'soft';
    if (n >= 100) return 'ok';
    if (n >= 50) return 'watch';
    return 'soft';
}

function resolveManabaseHeroName(profile, colorCount) {
    const strengths = Array.isArray(profile?.strengths) ? profile.strengths : [];
    for (const entry of strengths) {
        const name = MANABASE_HERO_STRENGTH_NAMES[entry?.id];
        if (name) return name;
    }
    return MANABASE_HERO_COLOR_NAMES[colorCount] || 'Manabase';
}

// ---- Profile marker copy ---------------------------------------------
// Opaque backend ids → user-facing template strings. Mirrors the pattern
// `BracketsPane.BRACKET_PROFILE_COPY` uses: each template takes `item.data`
// and returns the rendered text. Unknown ids are dropped silently (see
// `renderProfileItems` below) so new backend ids don't crash the UI.
const plural = (n, single, many) => `${n} ${n === 1 ? single : many}`;
const pct = (v) => `${Math.round((Number(v) || 0) * 100)}%`;

const COLOR_LABELS = { w: 'white', u: 'blue', b: 'black', r: 'red', g: 'green', c: 'colorless' };
const colorsList = (entries = []) =>
    entries
        .map((e) => COLOR_LABELS[e?.color] || e?.color)
        .filter(Boolean)
        .join(', ');

const MANABASE_PROFILE_COPY = {
    strengths: {
        pipCoverageHigh: ({ density }) => `Pip coverage is ${density} — every colored requirement is well-served.`,
        fixingQualityHigh: ({ density }) => `Land quality is ${density} — sources and fixing lean premium.`,
        untappedBase: ({ untappedCount, tapCount }) =>
            `Almost every land enters untapped — ${untappedCount} untapped vs ${tapCount} tapped.`,
        premiumFetchPackage: ({ count }) => `Premium fetch package — ${plural(count, 'fetch', 'fetches')}.`,
        originalDualPackage: ({ count }) => `Original-dual presence — ${plural(count, 'ABU dual', 'ABU duals')}.`,
        fastManaLandPackage: ({ count }) =>
            `Fast-mana lands — ${plural(count, 'Tomb-style land', 'Tomb-style lands')}.`,
        mdfcPackage: ({ count }) => `MDFC flexibility — ${plural(count, 'MDFC land', 'MDFC lands')}.`,
        utilityLandPackage: ({ count }) =>
            `Utility-land coverage — ${plural(count, 'non-mana utility land', 'non-mana utility lands')}.`,
        lowCurveMatchesBase: ({ avgCmc, untappedDensity }) =>
            `Low curve (${formatNumber(avgCmc)} avg CMC) matches a ${untappedDensity} untapped base.`,
        commanderAcceleration: ({ bonus }) =>
            `Commander itself accelerates mana (bonus: +${formatNumber(bonus / 100, 2)}).`,
        colorSourcesBalanced: ({ colorCount, requirementsMet }) =>
            `${colorCount}-color deck with ${requirementsMet} requirement fulfillment across colors.`,
        rainbowSourceDense: ({ count }) =>
            `Dense rainbow / any-color sources — ${plural(count, 'any-color producer', 'any-color producers')}.`,
        highTurnOneColorOdds: ({ odds }) => `${pct(odds)} chance of any needed color by turn 1.`,
        rampAbundant: ({ rockCount, dorkCount, ritualCount, landRampCount }) =>
            `Ramp is abundant — ${rockCount} rocks, ${dorkCount} dorks, ${ritualCount} rituals, ${landRampCount} land-ramp.`,
        landCountHealthy: ({ count }) => `Land count is in the standard band — ${count} lands.`,
        fetchDensityHigh: ({ count }) => `Fetch density is high — ${plural(count, 'fetch', 'fetches')}.`,
        curveAppealHigh: ({ score }) => `Curve-appeal score is dense (${formatNumber(score, 0)}).`,
        sourceCountSurplus: ({ sourceCount, expected, delta }) =>
            `Source count surplus — ${sourceCount} sources vs ${expected} expected (+${delta}).`,
    },
    risks: {
        manaDeficient: ({ severity, sourceCount, expected }) =>
            `Mana-deficient (${severity}) — ${sourceCount} sources vs ${expected} expected.`,
        tapLandHeavy: ({ count }) => `Tap-land heavy — ${plural(count, 'land', 'lands')} enter tapped.`,
        tapLandSevere: ({ count }) => `Tap-land load is severe — ${plural(count, 'land', 'lands')} enter tapped.`,
        fastManaMissingAtHighQuality: ({ fastManaLandCount }) =>
            `Quality capped: high base with ${fastManaLandCount} fast-mana lands.`,
        slowForLowCurve: ({ avgCmc, tapLandCount }) =>
            `Low curve (${formatNumber(avgCmc)}) paired with ${tapLandCount} tap lands — tempo mismatch.`,
        commanderPipStrain: ({ strain }) => `Commander pip demand is ${strain} on one color.`,
        colorFixingLightAtHighColorCount: ({ colorCount, overall }) =>
            `${colorCount}-color deck with ${overall} overall fixing — colors may strand.`,
        lowSourceCount: ({ sourceCount, expected, delta }) =>
            `Source count low — ${sourceCount} vs ${expected} expected (${delta}).`,
        curveAppealLow: ({ score }) => `Curve-appeal score is low (${formatNumber(score, 0)}).`,
        perColorDeficit: ({ colors }) =>
            colors?.length
                ? `Under-produced colors: ${colorsList(colors)}.`
                : 'One or more colors are under-produced.',
        landCountLow: ({ count }) => `Only ${count} lands — below the standard band.`,
        noFetchesMulticolor: ({ colorCount }) =>
            `${colorCount}-color deck with zero fetches — fixing is brittle.`,
        rampMissing: ({ totalRamp, avgCmc }) =>
            `Ramp missing — ${totalRamp} ramp pieces at avg CMC ${formatNumber(avgCmc)}.`,
    },
    improve: {
        addFetches: ({ currentCount, colorCount }) =>
            `Add fetches — ${currentCount} current vs a ${colorCount}-color deck.`,
        addPremiumDuals: ({ colorCount, currentQuality }) =>
            `Add premium duals — ${colorCount} colors with ${currentQuality} land quality.`,
        addFastManaLands: ({ currentCount }) =>
            `Add fast-mana lands (Tomb-style) — currently ${currentCount}.`,
        addMdfcLands: ({ currentCount }) => `Add MDFC lands — currently ${currentCount}.`,
        removeTapLands: ({ currentCount }) =>
            `Remove tap lands — ${currentCount} currently enter tapped.`,
        addUtilityLands: ({ currentCount }) =>
            `Add utility lands — ${currentCount} currently.`,
        addColorFixing: ({ requirementsMet, colorCount }) =>
            `Add color fixing — ${colorCount} colors with ${requirementsMet} fulfillment.`,
        increaseLandCount: ({ currentCount }) => `Increase land count — currently ${currentCount}.`,
        increaseRamp: ({ currentCount, avgCmc }) =>
            `Increase ramp — ${currentCount} pieces at avg CMC ${formatNumber(avgCmc)}.`,
        shoreUpColor: ({ color, coverageRatio, requirementCount }) =>
            `Shore up ${COLOR_LABELS[color] || color} — coverage ratio ${formatNumber(coverageRatio)} across ${requirementCount} requirements.`,
        diversifyColorSources: ({ colorCount }) =>
            `Diversify color sources — ${colorCount}-color deck has no rainbow producers.`,
    },
};

function renderProfileItems(items, category) {
    const map = MANABASE_PROFILE_COPY[category] || {};
    return (items || [])
        .map((item) => {
            const fn = map[item?.id];
            if (!fn) return null;
            try {
                const text = fn(item.data || {});
                return text ? { id: item.id, text } : null;
            } catch (err) {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn(`ManabasePane: failed to render ${category}.${item?.id}`, err);
                }
                return null;
            }
        })
        .filter(Boolean);
}

// ---- Ramp cost curve buckets (producer CMCs) -------------------------
const RAMP_COST_BUCKETS = [
    { key: '0', label: '0' },
    { key: '1', label: '1' },
    { key: '2', label: '2' },
    { key: '3', label: '3' },
    { key: '4+', label: '4+' },
];

const COLOR_SWATCH = {
    c: { label: 'Colorless', fill: CHART_MANA_COLORS.c, pip: 'c' },
    w: { label: 'White', fill: CHART_MANA_COLORS.w, pip: 'w' },
    u: { label: 'Blue', fill: CHART_MANA_COLORS.u, pip: 'u' },
    b: { label: 'Black', fill: CHART_MANA_COLORS.b, pip: 'b' },
    r: { label: 'Red', fill: CHART_MANA_COLORS.r, pip: 'r' },
    g: { label: 'Green', fill: CHART_MANA_COLORS.g, pip: 'g' },
};

const CARD_TYPE_BUCKETS = [
    { key: 'commanders', label: 'Commanders', match: (card) => card?.isCommander },
    {
        key: 'planeswalkers',
        label: 'Planeswalkers',
        match: (card) => !card?.isCommander && (card?.types || '').toString().toLowerCase().includes('planeswalker'),
    },
    {
        key: 'creatures',
        label: 'Creatures',
        match: (card) => !card?.isCommander && (card?.types || '').toString().toLowerCase().includes('creature'),
    },
    {
        key: 'sorceries',
        label: 'Sorceries',
        match: (card) => (card?.types || '').toString().toLowerCase().includes('sorcery'),
    },
    {
        key: 'instants',
        label: 'Instants',
        match: (card) => (card?.types || '').toString().toLowerCase().includes('instant'),
    },
    {
        key: 'artifacts',
        label: 'Artifacts',
        match: (card) =>
            !card?.isCommander &&
            (card?.types || '').toString().toLowerCase().includes('artifact') &&
            !(card?.types || '').toString().toLowerCase().includes('creature'),
    },
    {
        key: 'enchantments',
        label: 'Enchantments',
        match: (card) =>
            !card?.isCommander &&
            (card?.types || '').toString().toLowerCase().includes('enchantment') &&
            !(card?.types || '').toString().toLowerCase().includes('creature'),
    },
    {
        key: 'battles',
        label: 'Battles',
        match: (card) => (card?.types || '').toString().toLowerCase().includes('battle'),
    },
];

export function ManabasePane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const manabase = deck?.details?.manabase;
    const breakdown = manabase?.breakdown;
    const production = breakdown?.production?.breakdown;
    const debugManabase = manabase?.debugManabase;
    const manaProducers = manabase?.manaProducers;

    // Collapsible profile section — expanded on desktop (≥768px), collapsed on
    // mobile so the numeric/chart sections stay close to the top of the pane
    // on a phone. Matches the PowerLevelPane fingerprint behavior.
    const [profileOpen, setProfileOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    // ---- Quality scorecard — grade tiles with per-category thresholds.
    const qualityScorecard = useMemo(() => {
        const pct = manabase?.percentages;
        const thresholds = manabase?.thresholds;
        if (!pct || !thresholds) return [];
        return QUALITY_SCORES.map(({ key, label, hint }) => {
            const raw = parseFloat(pct?.[key]);
            const threshold = parseFloat(thresholds?.[key]);
            if (!Number.isFinite(raw) || !Number.isFinite(threshold) || threshold === 0) return null;
            // `overall` is reported on a 0–300 scale while the other three are
            // 0–100; normalize each against its own threshold so every row
            // sits on a shared 0–100%+ axis.
            const normalized = (raw / threshold) * 100;
            return { key, label, hint, raw, threshold, normalized };
        }).filter(Boolean);
    }, [manabase?.percentages, manabase?.thresholds]);

    // ---- Hero data — overall + subscores partitioned for the header.
    const overallRow = qualityScorecard.find((r) => r.key === 'overall') || null;
    const subscoreRows = qualityScorecard.filter((r) => r.key !== 'overall');
    const heroTone = manabaseHeroTone(overallRow?.normalized ?? 0);

    // ---- Profile stats strip — compact metrics that used to live in a
    // wide top row. Folded into the profile body so the hero can stay
    // tight while these details remain one click away.
    const profileStats = useMemo(() => {
        const rows = [];
        const lands = breakdown?.totalManaProducingLandCount;
        if (Number.isFinite(parseFloat(lands))) {
            rows.push({
                key: 'lands',
                label: 'Lands',
                value: formatInt(lands),
                hint: Number.isFinite(debugManabase?.expectedMinSources)
                    ? `vs ${formatInt(debugManabase.expectedMinSources)} expected`
                    : 'mana-producing',
            });
        }
        if (Number.isFinite(parseFloat(debugManabase?.totalManaSourceCount))) {
            rows.push({
                key: 'sources',
                label: 'Total sources',
                value: formatInt(debugManabase?.totalManaSourceCount),
                hint: 'lands + rocks + dorks',
            });
        }
        if (Number.isFinite(parseFloat(debugManabase?.fastManaLandCount))) {
            rows.push({
                key: 'fastmana',
                label: 'Fast mana',
                value: formatInt(debugManabase?.fastManaLandCount),
                hint: 'fast-mana lands',
            });
        }
        if (Number.isFinite(parseFloat(debugManabase?.avgCMC))) {
            rows.push({
                key: 'avgCmc',
                label: 'Avg CMC',
                value: formatNumber(debugManabase?.avgCMC),
                hint: 'nonland average',
            });
        }
        if (Number.isFinite(parseFloat(breakdown?.baseTotalCmc))) {
            rows.push({
                key: 'baseCmc',
                label: 'Base CMC',
                value: formatInt(breakdown?.baseTotalCmc),
                hint: 'sum across nonland',
            });
        }
        if ((debugManabase?.mdfcLandCount || 0) > 0) {
            rows.push({
                key: 'mdfc',
                label: 'MDFC lands',
                value: formatInt(debugManabase?.mdfcLandCount),
                hint: 'double-faced',
            });
        }
        const reducerCount = Object.keys(breakdown?.genericManaReducers || {}).length;
        if (reducerCount > 0) {
            rows.push({
                key: 'reducers',
                label: 'Cost reducers',
                value: formatInt(reducerCount),
                hint: 'generic mana',
            });
        }
        const reducedCards = manabase?.details?.totalReducedCostCards;
        if ((reducedCards || 0) > 0) {
            rows.push({
                key: 'reduced',
                label: 'Reduced cards',
                value: formatInt(reducedCards),
                hint: 'with cost reduction',
            });
        }
        return rows;
    }, [breakdown, debugManabase, manabase?.details?.totalReducedCostCards]);

    // ---- Profile-driven score drivers --------------------------------
    // The stable manabase profile exposes the same boost/penalty/net-direction
    // shape the power-level pane consumes. Deriving it here lets us reuse
    // `ScoreDriverRow` and the `.powerDrivers*` CSS verbatim.
    const profile = manabase?.profile;
    const scoreDrivers = useMemo(() => deriveManabaseScoreDrivers(profile), [profile]);

    // ---- Profile marker lists (strengths / risks / improve) ----------
    const strengthItems = useMemo(() => renderProfileItems(profile?.strengths, 'strengths'), [profile]);
    const riskItems = useMemo(() => renderProfileItems(profile?.risks, 'risks'), [profile]);
    const improveItems = useMemo(() => renderProfileItems(profile?.improve, 'improve'), [profile]);

    // Headline hint — the top backend strength reads as a user-friendly
    // one-liner, so we reuse it. Falls back to an improve suggestion if
    // the deck has no strengths, or a risk if neither. Avoids duplicating
    // the manabase marker copy templates.
    const heroHint =
        strengthItems[0]?.text ||
        improveItems[0]?.text ||
        riskItems[0]?.text ||
        null;

    // ---- Producer-level derived metrics (roster, ramp curve, specials).
    const producerStats = useMemo(() => {
        if (!manaProducers) return null;
        const entries = Object.values(manaProducers);
        const rampCurve = Object.fromEntries(RAMP_COST_BUCKETS.map((b) => [b.key, 0]));
        const fastMana = [];
        const anyColor = [];
        const singleUse = [];
        const dntAdd = [];
        let totalQuantity = 0;
        entries.forEach((p) => {
            if (!p) return;
            const qty = parseInt(p?.quantity, 10) || 1;
            totalQuantity += qty;
            const cmc = parseInt(p?.cmc, 10);
            const bucketKey = !Number.isFinite(cmc) ? null : cmc >= 4 ? '4+' : String(cmc);
            if (bucketKey != null) rampCurve[bucketKey] += qty;
            if (p.isFastMana) fastMana.push({ ...p, qty });
            if (p.isAnyColor) anyColor.push({ ...p, qty });
            if (p.isSingleUse) singleUse.push({ ...p, qty });
            if (p.doesNotTapToAdd) dntAdd.push({ ...p, qty });
        });
        const rampCurveRows = RAMP_COST_BUCKETS.map(({ key, label }) => ({
            manaCost: label,
            count: rampCurve[key],
        }));
        return {
            totalQuantity,
            rampCurveRows,
            fastMana: fastMana.sort((a, b) => (a.cmc || 0) - (b.cmc || 0)),
            anyColor: anyColor.sort((a, b) => (a.cmc || 0) - (b.cmc || 0)),
            singleUse: singleUse.sort((a, b) => (a.cmc || 0) - (b.cmc || 0)),
            dntAdd: dntAdd.sort((a, b) => (a.cmc || 0) - (b.cmc || 0)),
        };
    }, [manaProducers]);

    // ---- Tempo laggards — cards whose earliest reliable cast is later
    // than their own CMC. Efficiency < 1 is the clean signal: the engine
    // reports efficiency as (cmc / earliestTurnCast) and it drops below 1
    // when the deck can't reach the mana to cast a card on curve.
    const tempoLaggards = useMemo(() => {
        const cardProbs = manabase?.probabilities?.cards;
        if (!cardProbs || !cards) return [];
        const rows = [];
        Object.entries(cardProbs).forEach(([id, entry]) => {
            if (!entry) return;
            const earliest = parseInt(entry?.earliestTurnCast, 10);
            const onCurve = parseInt(entry?.onCurveTurnToCast, 10);
            const efficiency = parseFloat(entry?.efficiency);
            const card = cards[id];
            const cmc = parseInt(card?.manaValue ?? card?.cmc ?? card?.convertedManaCost, 10);
            if (!Number.isFinite(earliest) || !Number.isFinite(cmc)) return;
            // A "laggard" either can't reach its CMC turn reliably, or the
            // deck's earliest possible cast already slips past its natural
            // curve. Ignore commanders — they live in the command zone and
            // always cast on their own schedule.
            if (card?.isCommander) return;
            const slip = earliest - cmc;
            if (slip <= 0 && (!Number.isFinite(efficiency) || efficiency >= 1)) return;
            rows.push({
                id,
                name: card?.name || id,
                cmc,
                earliest,
                onCurve: Number.isFinite(onCurve) ? onCurve : earliest,
                efficiency: Number.isFinite(efficiency) ? efficiency : null,
                slip,
            });
        });
        return rows.sort((a, b) => {
            if (b.slip !== a.slip) return b.slip - a.slip;
            return (a.efficiency || 1) - (b.efficiency || 1);
        });
    }, [manabase?.probabilities?.cards, cards]);

    // ---- getCategories powers the card-list categories on the left side
    // panel via BaseDeckPane. Each score bucket → list of cards in that role.
    const getCategories = () => {
        const list = {};
        Object.keys(CATEGORY_CONSTANTS)
            .sort((a, b) =>
                CATEGORY_CONSTANTS[a].label.toLowerCase() < CATEGORY_CONSTANTS[b].label.toLowerCase() ? -1 : 1
            )
            .forEach((category) => {
                const bucket = breakdown?.[category];
                if (!bucket || Object.keys(bucket).length === 0) return;
                const items = Object.keys(bucket).map((id) => ({
                    score: parseFloat(bucket[id] || '0.00').toFixed(2),
                    ...cards?.[id],
                }));
                list[category] = {
                    label: CATEGORY_CONSTANTS[category].label,
                    id: category,
                    count: items.length,
                    list: items,
                    color: '#FFFFFF',
                    subCategories: [],
                };
            });
        return list;
    };

    // ---- Per-color mana pip production vs requirement ----
    // When the profile is populated, prefer its per-color data for ratio
    // and surplus/deficit flags — those are the stable, backend-canonical
    // reads (the legacy `production.breakdown` is kept as the numeric
    // source of truth for the expanded detail panel).
    const colorRows = useMemo(() => {
        if (!production) return [];
        const perColorProfile = profile?.fixing?.perColor || {};
        return Object.keys(production).map((color) => {
            const data = production[color] || {};
            const meta = COLOR_SWATCH[color] || { label: color.toUpperCase(), fill: '#999', pip: color };
            const rawReq = parseFloat(data?.percentages?.requirements) || 0;
            const rawProd = parseFloat(data?.percentages?.production) || 0;
            const avgReq = Math.round(rawReq * 100);
            const avgProd = Math.round(rawProd * 100);
            const profileEntry = perColorProfile[color] || null;
            // Prefer the raw ratio the engine ships — it's computed before
            // the rounded percentages lose precision.
            const rawRatio = parseFloat(
                profileEntry?.coverageRatio ?? data?.percentages?.productionToRequirementRatio
            );
            const ratio = Number.isFinite(rawRatio) ? rawRatio : rawReq === 0 ? null : rawProd / rawReq;
            const commanderPipRatio = parseFloat(
                profileEntry?.commanderPipRatio ?? data?.percentages?.commanderPipRatio
            );
            const details = {
                coverageDensity: profileEntry?.coverage || null,
                surplus: !!profileEntry?.surplus,
                deficit: !!profileEntry?.deficit,
                requirementsCount: data?.requirements?.count,
                averageDensity: data?.requirements?.averageDensity,
                productionCount: data?.production?.count,
                totalProduced: data?.production?.totalProduced,
                averageProduced: data?.production?.averageProduced,
                commanderPipCount: data?.commanderPipCount,
                commanderPipRatio: Number.isFinite(commanderPipRatio) ? commanderPipRatio : null,
                productionToRequirementRatio: ratio,
                slowQuantity: data?.production?.details?.slowQuantity,
                basicLands: data?.production?.details?.basicLands,
                fetchableLands: data?.production?.details?.fetchableLands,
                fetchesCount: data?.production?.details?.fetchesCount,
                basicFetchesCount: data?.production?.details?.basicFetchesCount,
                landRampCount: data?.production?.details?.landRampCount,
                producerTypes: data?.production?.types || {},
            };
            return {
                color,
                meta,
                avgReq,
                avgProd,
                avgSources: data?.production?.count || 0,
                ratio,
                coverageDensity: profileEntry?.coverage || null,
                surplus: !!profileEntry?.surplus,
                deficit: !!profileEntry?.deficit,
                details,
            };
        });
    }, [production, profile?.fixing?.perColor]);

    // Distinct WUBRG colors produced by the deck — fallback input for the
    // hero name when no profile trait is dominant. Colorless-only entries
    // are excluded so a 5-color Domain deck doesn't get called "6-color."
    const deckColorCount = useMemo(
        () => colorRows.filter((r) => r.color !== 'c' && r.avgProd > 0).length,
        [colorRows]
    );

    const heroName = useMemo(
        () => resolveManabaseHeroName(profile, deckColorCount),
        [profile, deckColorCount]
    );

    // ---- Opening-hand probabilities (per-color + fast mana) ----
    const openingHandData = useMemo(() => {
        const handObject = manabase?.probabilities?.openingHand;
        if (!handObject) return { data: [], colors: [] };
        const colors = Object.keys(handObject?.color || {});
        const data = [];
        for (let i = 0; i < 8; i++) {
            const row = { name: `${i}` };
            colors.forEach((color) => {
                row[color.toUpperCase()] = Math.round((handObject.color[color]?.[i] || 0) * 100);
            });
            row.Fastmana = Math.round((handObject?.fastmana?.[i] || 0) * 100);
            data.push(row);
        }
        return { data, colors };
    }, [manabase?.probabilities?.openingHand]);

    // ---- On-curve castability (actual vs expected by turn) ----
    const curveData = useMemo(() => {
        const turns = manabase?.curveChart?.turns;
        if (!turns) return [];
        const rows = [];
        for (let i = 1; i < 9; i++) {
            const curveObject = turns?.[i];
            if (!curveObject) continue;
            rows.push({
                turn: i,
                Expected: Math.round((parseFloat(curveObject?.baseLinePercentage) || 0) * 100),
                Actual: Math.round((parseFloat(curveObject?.actualPercentage) || 0) * 100),
            });
        }
        return rows;
    }, [manabase?.curveChart?.turns]);

    // ---- Mana value distribution (NEW for deck pane, matches commander) ----
    // Build a nonland card count at each mana value from `cards`.
    const manaValueDistribution = useMemo(() => {
        const counts = new Map();
        Object.values(cards || {}).forEach((card) => {
            if (!card) return;
            const types = (card?.types || '').toString().toLowerCase();
            if (types.includes('land')) return;
            if (card?.isCommander) return;
            if (card?.parseIndividually === false) return;
            const mv = parseInt(card?.manaValue ?? card?.cmc ?? card?.convertedManaCost, 10);
            if (Number.isNaN(mv)) return;
            const key = mv >= 7 ? 7 : mv;
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        const rows = [];
        for (let i = 0; i <= 7; i++) {
            const label = i === 7 ? '7+' : `${i}`;
            rows.push({ manaValue: label, count: counts.get(i) || 0 });
        }
        return rows;
    }, [cards]);

    // ---- Source-type breakdown (NEW for deck pane, matches commander) ----
    // Derived from the score bucket card counts.
    const sourcesData = useMemo(() => {
        const rows = [
            { key: 'lands', label: 'Lands', count: breakdown?.totalManaProducingLandCount || 0 },
            { key: 'rocks', label: 'Mana rocks', count: Object.keys(breakdown?.rockScores || {}).length },
            { key: 'dorks', label: 'Mana dorks', count: Object.keys(breakdown?.dorkScores || {}).length },
            { key: 'rituals', label: 'Rituals', count: Object.keys(breakdown?.ritualScores || {}).length },
            { key: 'treasure', label: 'Treasure', count: Object.keys(breakdown?.treasureScores || {}).length },
            { key: 'landRamp', label: 'Land ramp', count: Object.keys(breakdown?.landRampScores || {}).length },
            { key: 'other', label: 'Other', count: Object.keys(breakdown?.otherScores || {}).length },
        ];
        return rows.filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
    }, [breakdown]);

    // ---- Tap land details (only present when the engine found tap
    // lands worth penalizing). `debugManabase.tapLandDetails` is the
    // receipt for `tapLandPenalty` / `effectiveTapLandPenalty`.
    const tapLandRows = useMemo(() => {
        const details = debugManabase?.tapLandDetails;
        if (!Array.isArray(details) || details.length === 0) return [];
        return details
            .map((entry) => {
                const id = entry?.containerId || entry?.id;
                const name = entry?.name || cards?.[id]?.name || id;
                return { id, name, raw: entry };
            })
            .filter((r) => r.id || r.name);
    }, [debugManabase?.tapLandDetails, cards]);

    // ---- Per-card castability dropdown wiring ----
    const cardBuckets = useMemo(() => {
        if (!cards) return [];
        const buckets = new Map();
        CARD_TYPE_BUCKETS.forEach((b) => buckets.set(b.key, { label: b.label, children: [] }));
        Object.values(cards).forEach((card) => {
            if (!card) return;
            if (card?.parseIndividually === false) return;
            const types = (card?.types || '').toString().toLowerCase();
            if (types.includes('land') && !card?.isCommander) return;
            for (const bucket of CARD_TYPE_BUCKETS) {
                if (bucket.match(card)) {
                    buckets.get(bucket.key).children.push({ id: card.id, name: card.name });
                    break;
                }
            }
        });
        return Array.from(buckets.entries())
            .filter(([, b]) => b.children.length > 0)
            .map(([key, b]) => ({
                id: `${key}Cards`,
                name: b.label,
                children: b.children.sort((a, b2) => (a.id < b2.id ? -1 : 1)),
            }));
    }, [cards]);

    const firstCardId = cardBuckets?.[0]?.children?.[0]?.id || null;
    const [dropDownCardId, setDropDownCardId] = useState(firstCardId);
    const effectiveCardId = dropDownCardId || firstCardId;

    const perCardData = useMemo(() => {
        const cardProb = manabase?.probabilities?.cards?.[effectiveCardId];
        if (!cardProb?.turns) return [];
        const rows = [];
        for (let i = 1; i < 9; i++) {
            const turn = cardProb.turns[i];
            if (turn == null) continue;
            const inHand = Math.round((parseFloat(turn?.inHand ?? turn) || 0) * 100);
            rows.push({ turn: i, inHand });
        }
        return rows;
    }, [manabase?.probabilities?.cards, effectiveCardId]);

    const activeCardTempo = useMemo(() => {
        const entry = manabase?.probabilities?.cards?.[effectiveCardId];
        if (!entry) return null;
        const earliest = parseInt(entry?.earliestTurnCast, 10);
        const onCurve = parseInt(entry?.onCurveTurnToCast, 10);
        const efficiency = parseFloat(entry?.efficiency);
        return {
            earliest: Number.isFinite(earliest) ? earliest : null,
            onCurve: Number.isFinite(onCurve) ? onCurve : null,
            efficiency: Number.isFinite(efficiency) ? efficiency : null,
        };
    }, [manabase?.probabilities?.cards, effectiveCardId]);

    // Build MTG color palette for opening-hand area lines.
    const colorLineStroke = (symbol) => CHART_MANA_COLORS[symbol?.toLowerCase()] || '#bfc7da';

    // ---- Render ----
    return (
        <BaseDeckPane title="Manabase" content={<div />} showScore={false} categories={getCategories()}>
            <Flex direction="column" rowGap={24} marginTop={10}>
                {/* ===== HERO HEADER =====
                    Mirrors the salt personality header: eyebrow + big
                    headline name on the left, overall score box + the
                    three subscore chips (Fixing / Quality / Curve) on
                    the right. No gauge / tier-warning language — the
                    headline reads as the deck's manabase identity. */}
                {overallRow && (
                    <HeroPanel
                        tone="green"
                        className="manabaseHero"
                        data-tier={heroTone}
                        eyebrow="Manabase"
                        title={heroName}
                        hint={heroHint || undefined}
                        aside={
                            <>
                                <HeroPanelTotal
                                    label="Manabase score"
                                    value={formatNumber(overallRow.raw, 0)}
                                    sub={`/ ${formatNumber(overallRow.threshold, 0)} · ${Math.round(overallRow.normalized)}%`}
                                />
                                {subscoreRows.length > 0 && (
                                    <div className="manabaseHeroTags">
                                        {subscoreRows.map((row) => (
                                            <span
                                                key={row.key}
                                                className={`manabaseHeroTag${
                                                    row.normalized >= 100 ? ' is-over' : ''
                                                }`}
                                                title={`${row.hint} · threshold ${formatNumber(
                                                    row.threshold,
                                                    0
                                                )}`}
                                            >
                                                <span className="manabaseHeroTagLabel">{row.label}</span>
                                                <span className="manabaseHeroTagValue">
                                                    {formatNumber(row.raw, 0)}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        }
                    />
                )}

                {/* ===== MANABASE PROFILE (collapsible) ===== */}
                {/* Unifies the strengths/risks/improve marker lists with the */}
                {/* canonical score drivers into one collapsible section. */}
                {/* Reuses the powerSection* / powerDrivers* CSS from */}
                {/* PowerLevelPane so both "why this score" surfaces read as a */}
                {/* family. Starts expanded on desktop, collapsed on mobile. */}
                {(strengthItems.length > 0 ||
                    riskItems.length > 0 ||
                    improveItems.length > 0 ||
                    scoreDrivers?.hasAnyContent ||
                    profileStats.length > 0) && (
                    <section
                        className={`powerSection powerSection--collapsible${
                            profileOpen ? ' is-open' : ''
                        }`}
                    >
                        <button
                            type="button"
                            className="powerSectionToggle"
                            aria-expanded={profileOpen}
                            aria-controls="manabaseProfileBody"
                            onClick={() => setProfileOpen((v) => !v)}
                        >
                            <span className="powerSectionEyebrow">
                                <span className="powerSectionChevron" aria-hidden="true">
                                    {profileOpen ? '▾' : '▸'}
                                </span>
                                Manabase profile
                            </span>
                            <span className="powerSectionHint">
                                Why this manabase scored the way it did — signals the scorer rewarded, concerns it
                                flagged, suggestions to raise the score, and the magnitude-coded boosts and penalties
                                behind the final number.
                            </span>
                        </button>

                        {profileOpen && (
                            <div id="manabaseProfileBody" className="powerSectionBody">
                                {profileStats.length > 0 && (
                                    <div className="manabaseProfileStats">
                                        {profileStats.map((stat) => (
                                            <div key={stat.key} className="manabaseProfileStat">
                                                <span className="manabaseProfileStatLabel">{stat.label}</span>
                                                <span className="manabaseProfileStatValue">{stat.value}</span>
                                                <span className="manabaseProfileStatHint">{stat.hint}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {(strengthItems.length > 0 ||
                                    riskItems.length > 0 ||
                                    improveItems.length > 0) && (
                                    <div className="manabaseProfileGrid">
                                        <ProfileMarkerColumn
                                            tone="up"
                                            title="Strengths"
                                            subtitle="What the scorer rewarded"
                                            items={strengthItems}
                                            emptyText="No scoring strengths fired for this deck."
                                        />
                                        <ProfileMarkerColumn
                                            tone="down"
                                            title="Risks"
                                            subtitle="Concerns dragging the score"
                                            items={riskItems}
                                            emptyText="No manabase risks flagged."
                                        />
                                        <ProfileMarkerColumn
                                            tone="flat"
                                            title="Suggestions"
                                            subtitle="Changes that would raise the score"
                                            items={improveItems}
                                            emptyText="No suggestions — the scorer has no obvious upgrades."
                                        />
                                    </div>
                                )}

                                {scoreDrivers?.hasAnyContent && (
                                    <div className="powerDrivers">
                                        <div className="powerDriversHeader">
                                            <span className="powerDriversTitle">Score drivers</span>
                                            <span
                                                className={`powerDriversNet powerDriversNet--${scoreDrivers.netDirection}`}
                                            >
                                                {scoreDrivers.netDirection === 'up' && '▲'}
                                                {scoreDrivers.netDirection === 'down' && '▼'}
                                                {scoreDrivers.netDirection === 'flat' && '═'}{' '}
                                                {scoreDrivers.netCopy}
                                            </span>
                                        </div>
                                        {scoreDrivers.caps.length > 0 && (
                                            <div className="manabaseCapsRow">
                                                <span className="manabaseCapsLabel">Caps applied</span>
                                                {scoreDrivers.caps.map((cap) => (
                                                    <span key={cap.key} className="manabaseCapBadge">
                                                        {cap.label}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="powerDriversGrid">
                                            <div className="powerDriversColumn powerDriversColumn--boosts">
                                                <span className="powerDriversColumnHeader">
                                                    ▲ Pushed the score up
                                                </span>
                                                {scoreDrivers.boosts.length === 0 ? (
                                                    <span className="powerDriversEmpty">
                                                        No boosts fired for this deck.
                                                    </span>
                                                ) : (
                                                    scoreDrivers.boosts.map((entry) => (
                                                        <ScoreDriverRow
                                                            key={entry.key}
                                                            entry={entry}
                                                            direction="boost"
                                                        />
                                                    ))
                                                )}
                                            </div>
                                            <div className="powerDriversColumn powerDriversColumn--penalties">
                                                <span className="powerDriversColumnHeader">
                                                    ▼ Pulled the score down
                                                </span>
                                                {scoreDrivers.penalties.length === 0 ? (
                                                    <span className="powerDriversEmpty">
                                                        No penalties fired for this deck.
                                                    </span>
                                                ) : (
                                                    scoreDrivers.penalties.map((entry) => (
                                                        <ScoreDriverRow
                                                            key={entry.key}
                                                            entry={entry}
                                                            direction="penalty"
                                                        />
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {/* ===== ON-CURVE CASTABILITY ===== */}
                {curveData.length > 0 && (
                    <StatsSection
                        title="On-curve castability"
                        subtitle="Actual vs expected % of cards castable on each turn"
                        helpHeading="Castability by Turn"
                        helpContent={
                            <Flex direction="column" rowGap={8}>
                                <Text>
                                    <b>Expected</b> is how the deck would play if every card were cast on-curve (2-drops
                                    on turn 2, etc.).
                                </Text>
                                <Text>
                                    <b>Actual</b> is the statistical count of cards per turn that are actually castable
                                    given your manabase.
                                </Text>
                            </Flex>
                        }
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={240}>
                                <AreaChart data={curveData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                    <defs>
                                        {searedAreaGradient({
                                            id: gradientId('curve-actual'),
                                            color: CHART_ACCENT_GOLD,
                                        })}
                                        {searedAreaGradient({
                                            id: gradientId('curve-expected'),
                                            color: CHART_INK_MUTED,
                                        })}
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray={CHART_GRID_STROKE_DASH}
                                        stroke={CHART_GRID_STROKE}
                                    />
                                    <XAxis dataKey="turn" {...CHART_AXIS_STYLE}>
                                        <Label
                                            value="Turn"
                                            offset={-6}
                                            position="insideBottom"
                                            style={CHART_LABEL_STYLE}
                                        />
                                    </XAxis>
                                    <YAxis
                                        {...CHART_AXIS_STYLE}
                                        domain={[0, 100]}
                                        width={38}
                                        tickFormatter={(v) => `${v}%`}
                                    />
                                    <Tooltip
                                        content={
                                            <SearedTooltip
                                                labelFormatter={(v) => `Turn ${v}`}
                                                valueFormatter={(v) => `${v}%`}
                                            />
                                        }
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Legend
                                        wrapperStyle={CHART_LEGEND_WRAPPER_STYLE}
                                        iconType="circle"
                                        iconSize={9}
                                        formatter={searedLegendFormatter}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="Expected"
                                        stroke={CHART_INK_MUTED}
                                        strokeWidth={2}
                                        strokeDasharray="4 3"
                                        fill={`url(#${gradientId('curve-expected')})`}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="Actual"
                                        stroke={CHART_ACCENT_GOLD}
                                        strokeWidth={2.5}
                                        fill={`url(#${gradientId('curve-actual')})`}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== TEMPO LAGGARDS ===== */}
                {tempoLaggards.length > 0 && (
                    <StatsSection
                        title="Tempo laggards"
                        subtitle={`${tempoLaggards.length} card${
                            tempoLaggards.length === 1 ? '' : 's'
                        } whose earliest reliable cast slips past its mana value`}
                        helpHeading="Tempo laggards"
                        helpContent={
                            <Flex direction="column" rowGap={6}>
                                <Text>
                                    These cards can't hit their natural curve given this manabase — the earliest the
                                    engine expects them to cast is later than their mana value.
                                </Text>
                                <Text>
                                    The <b>slip</b> column shows how many turns past the card's MV the earliest cast
                                    lands. Efficiency &lt; 1 means the deck is struggling to reach the colors.
                                </Text>
                            </Flex>
                        }
                    >
                        <TempoLaggardList rows={tempoLaggards.slice(0, 12)} />
                    </StatsSection>
                )}

                {/* ===== MANA VALUE DISTRIBUTION ===== */}
                {manaValueDistribution.some((r) => r.count > 0) && (
                    <StatsSection
                        title="Mana value distribution"
                        subtitle="Nonland card count at each mana value (7+ is bucketed)"
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart
                                    data={manaValueDistribution}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray={CHART_GRID_STROKE_DASH}
                                        stroke={CHART_GRID_STROKE}
                                    />
                                    <XAxis dataKey="manaValue" {...CHART_AXIS_STYLE}>
                                        <Label
                                            value="Mana value"
                                            offset={-6}
                                            position="insideBottom"
                                            style={CHART_LABEL_STYLE}
                                        />
                                    </XAxis>
                                    <YAxis {...CHART_AXIS_STYLE} width={38} allowDecimals={false} />
                                    <Tooltip
                                        content={
                                            <SearedTooltip
                                                labelFormatter={(v) => `MV ${v}`}
                                                valueFormatter={(v) => `${v} card${v === 1 ? '' : 's'}`}
                                            />
                                        }
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Bar dataKey="count" fill={CHART_ACCENT_RED} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== OPENING HAND PROBABILITIES ===== */}
                {openingHandData.data.length > 0 && openingHandData.colors.length > 0 && (
                    <StatsSection
                        title="Opening hand probabilities"
                        subtitle="% chance of N mana producers of each color (plus fast mana) in a 7-card opener"
                        helpHeading="Pip color percentages"
                        helpContent={
                            <Flex direction="column" rowGap={8}>
                                <Text>
                                    Shows how likely you are to have N mana-producing lands (including fetch lands) of a
                                    given color in your opening hand.
                                </Text>
                                <Text>
                                    The x-axis is the <b>quantity</b> of cards producing that pip. Fast mana is included
                                    as a separate line.
                                </Text>
                            </Flex>
                        }
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart
                                    data={openingHandData.data}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray={CHART_GRID_STROKE_DASH}
                                        stroke={CHART_GRID_STROKE}
                                    />
                                    <XAxis dataKey="name" {...CHART_AXIS_STYLE}>
                                        <Label
                                            value="# of mana producers in opener"
                                            offset={-6}
                                            position="insideBottom"
                                            style={CHART_LABEL_STYLE}
                                        />
                                    </XAxis>
                                    <YAxis
                                        {...CHART_AXIS_STYLE}
                                        domain={[0, 100]}
                                        width={38}
                                        tickFormatter={(v) => `${v}%`}
                                    />
                                    <Tooltip
                                        content={
                                            <SearedTooltip
                                                labelFormatter={(v) => `${v} source${v === '1' ? '' : 's'}`}
                                                valueFormatter={(v) => `${v}%`}
                                            />
                                        }
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Legend
                                        wrapperStyle={CHART_LEGEND_WRAPPER_STYLE}
                                        iconType="circle"
                                        iconSize={9}
                                        formatter={searedLegendFormatter}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="Fastmana"
                                        stroke={CHART_ACCENT_GOLD}
                                        strokeWidth={2}
                                        fillOpacity={0}
                                    />
                                    {openingHandData.colors.map((color) => (
                                        <Area
                                            key={color}
                                            type="monotone"
                                            dataKey={color.toUpperCase()}
                                            stroke={colorLineStroke(color)}
                                            strokeWidth={2.5}
                                            fillOpacity={0}
                                        />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== PER-CARD CASTABILITY ===== */}
                {cardBuckets.length > 0 && (
                    <StatsSection
                        title="Card castability"
                        subtitle="Odds of being able to cast a specific card by turn"
                        helpHeading="Card castability"
                        helpContent={
                            <Flex direction="column" rowGap={6}>
                                <Text>Statistical probability of casting this card on turns 1–8, accounting for:</Text>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    <li>Mana-producing lands (including fetches) of the right pip colors</li>
                                    <li>Pip density per color</li>
                                    <li>Number of distinct colors required relative to turn</li>
                                    <li>Predicted fast mana &amp; ramp availability</li>
                                </ul>
                            </Flex>
                        }
                    >
                        <Flex direction="column" rowGap={12}>
                            <Flex direction="row" alignItems="center" columnGap={12} wrap>
                                <MenuTrigger>
                                    <ActionButton UNSAFE_style={{ maxWidth: 320 }}>
                                        {cards?.[effectiveCardId]?.name || 'Pick a card'}
                                    </ActionButton>
                                    <Menu
                                        items={cardBuckets}
                                        selectedKeys={[effectiveCardId]}
                                        onSelectionChange={(evn) => setDropDownCardId(Array.from(evn)[0])}
                                        selectionMode="single"
                                    >
                                        {(bucket) => (
                                            <Section items={bucket.children} title={bucket.name}>
                                                {(card) => <Item key={card.id}>{card.name}</Item>}
                                            </Section>
                                        )}
                                    </Menu>
                                </MenuTrigger>
                                {activeCardTempo?.earliest != null && (
                                    <div className="manabaseCardTempoRow">
                                        <span className="manabaseCardTempoPill">
                                            <span className="manabaseCardTempoLabel">Earliest</span>
                                            <span className="manabaseCardTempoValue">T{activeCardTempo.earliest}</span>
                                        </span>
                                        {activeCardTempo.onCurve != null && (
                                            <span className="manabaseCardTempoPill">
                                                <span className="manabaseCardTempoLabel">On-curve</span>
                                                <span className="manabaseCardTempoValue">
                                                    T{activeCardTempo.onCurve}
                                                </span>
                                            </span>
                                        )}
                                        {activeCardTempo.efficiency != null && (
                                            <span
                                                className={`manabaseCardTempoPill ${
                                                    activeCardTempo.efficiency < 1
                                                        ? 'is-deficit'
                                                        : activeCardTempo.efficiency > 1
                                                        ? 'is-bonus'
                                                        : ''
                                                }`}
                                            >
                                                <span className="manabaseCardTempoLabel">Efficiency</span>
                                                <span className="manabaseCardTempoValue">
                                                    {formatNumber(activeCardTempo.efficiency)}
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Flex>
                            {perCardData.length > 0 && (
                                <div className="manabaseChartWrap">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart
                                            data={perCardData}
                                            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                                        >
                                            <defs>
                                                {searedAreaGradient({
                                                    id: gradientId('percard'),
                                                    color: CHART_ACCENT_RED,
                                                })}
                                            </defs>
                                            <CartesianGrid
                                                strokeDasharray={CHART_GRID_STROKE_DASH}
                                                stroke={CHART_GRID_STROKE}
                                            />
                                            <XAxis dataKey="turn" {...CHART_AXIS_STYLE}>
                                                <Label
                                                    value="Turn"
                                                    offset={-6}
                                                    position="insideBottom"
                                                    style={CHART_LABEL_STYLE}
                                                />
                                            </XAxis>
                                            <YAxis
                                                {...CHART_AXIS_STYLE}
                                                domain={[0, 100]}
                                                width={38}
                                                tickFormatter={(v) => `${v}%`}
                                            />
                                            <Tooltip
                                                content={
                                                    <SearedTooltip
                                                        labelFormatter={(v) => `Turn ${v}`}
                                                        valueFormatter={(v) => `${v}%`}
                                                    />
                                                }
                                                cursor={SEARED_TOOLTIP_CURSOR}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="inHand"
                                                name="Castable"
                                                stroke={CHART_ACCENT_RED}
                                                strokeWidth={2.5}
                                                fill={`url(#${gradientId('percard')})`}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Flex>
                    </StatsSection>
                )}

                {/* ===== RAMP COST CURVE ===== */}
                {producerStats?.rampCurveRows?.some((r) => r.count > 0) && (
                    <StatsSection
                        title="Ramp cost curve"
                        subtitle="Copies of mana producers at each casting cost (lands count as 0)"
                        helpHeading="Ramp cost curve"
                        helpContent={
                            <Text>
                                Distribution of every mana-producing card (lands, rocks, dorks, rituals, treasure
                                makers) bucketed by their own mana value. A heavy left side means the deck comes online
                                fast; a right-skewed curve suggests ramp that's slow to deploy.
                            </Text>
                        }
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart
                                    data={producerStats.rampCurveRows}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray={CHART_GRID_STROKE_DASH}
                                        stroke={CHART_GRID_STROKE}
                                    />
                                    <XAxis dataKey="manaCost" {...CHART_AXIS_STYLE}>
                                        <Label
                                            value="Producer mana cost"
                                            offset={-6}
                                            position="insideBottom"
                                            style={CHART_LABEL_STYLE}
                                        />
                                    </XAxis>
                                    <YAxis {...CHART_AXIS_STYLE} width={38} allowDecimals={false} />
                                    <Tooltip
                                        content={
                                            <SearedTooltip
                                                labelFormatter={(v) => `MV ${v}`}
                                                valueFormatter={(v) => `${v} producer${v === 1 ? '' : 's'}`}
                                            />
                                        }
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Bar dataKey="count" fill={CHART_ACCENT_GOLD} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== PRODUCER HIGHLIGHTS ===== */}
                {producerStats &&
                    (producerStats.fastMana.length > 0 ||
                        producerStats.anyColor.length > 0 ||
                        producerStats.singleUse.length > 0 ||
                        producerStats.dntAdd.length > 0) && (
                        <StatsSection
                            title="Producer highlights"
                            subtitle="Special-purpose mana sources picked out of the deck"
                            helpHeading="Highlights"
                            helpContent={
                                <Flex direction="column" rowGap={6}>
                                    <Text>
                                        <b>Fast mana</b> — producers that make mana the turn they come down (Sol Ring,
                                        Mana Vault, Ancient Tomb, signets, etc.).
                                    </Text>
                                    <Text>
                                        <b>Any color</b> — producers that can tap for any color your deck needs (Command
                                        Tower, Mana Confluence, City of Brass, etc.).
                                    </Text>
                                    <Text>
                                        <b>Single use</b> — one-shot mana (rituals, Lotus Petal, Chrome Mox).
                                    </Text>
                                    <Text>
                                        <b>Doesn't tap</b> — mana sources that don't tap themselves to add mana (useful
                                        for untap engines and floating mana).
                                    </Text>
                                </Flex>
                            }
                        >
                            <div className="manabaseHighlightGroups">
                                <ProducerChipGroup label="Fast mana" entries={producerStats.fastMana} cards={cards} />
                                <ProducerChipGroup label="Any color" entries={producerStats.anyColor} cards={cards} />
                                <ProducerChipGroup label="Single use" entries={producerStats.singleUse} cards={cards} />
                                <ProducerChipGroup label="Doesn't tap" entries={producerStats.dntAdd} cards={cards} />
                            </div>
                        </StatsSection>
                    )}

                {/* ===== MANA SOURCE BREAKDOWN ===== */}
                {sourcesData.length > 0 && (
                    <StatsSection title="Mana source breakdown" subtitle="Card count per source category">
                        <div className="manabaseSourceList">
                            {sourcesData.map((row) => {
                                const max = sourcesData[0]?.count || 1;
                                const pct = Math.max(4, (row.count / max) * 100);
                                return (
                                    <div key={row.key} className="manabaseSourceRow">
                                        <span className="manabaseSourceLabel">{row.label}</span>
                                        <div className="manabaseSourceBarTrack">
                                            <div className="manabaseSourceBarFill" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="manabaseSourceValue">{row.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </StatsSection>
                )}

                {/* ===== COLOR PRODUCTION VS REQUIREMENT ===== */}
                {colorRows.length > 0 && (
                    <StatsSection
                        title="Color production vs requirement"
                        subtitle="Red bars = under-producing vs what the deck's spells demand"
                    >
                        <div className="manabaseColorList">
                            {colorRows.map((row) => {
                                // Prefer the profile flags when available — they account for
                                // hysteresis around the 1.0 ratio line. Fall back to the raw
                                // ratio, then to the rounded % comparison.
                                const deficit =
                                    row.deficit ||
                                    (row.ratio != null ? row.ratio < 1 : row.avgProd < row.avgReq);
                                const surplus = row.surplus && !deficit;
                                const statusLabel = deficit
                                    ? 'Under'
                                    : surplus
                                    ? 'Surplus'
                                    : 'OK';
                                const statusClass = deficit
                                    ? 'is-deficit'
                                    : surplus
                                    ? 'is-surplus'
                                    : 'is-healthy';
                                const max = Math.max(row.avgReq, row.avgProd, 1);
                                return (
                                    <div key={row.color} className="manabaseColorRow">
                                        <div className="manabaseColorHeader">
                                            <span className={`mana medium s${row.color}`} />
                                            <span className="manabaseColorName">{row.meta.label}</span>
                                            {row.coverageDensity && row.coverageDensity !== 'none' && (
                                                <span
                                                    className={`manabaseColorDensity manabaseColorDensity--${row.coverageDensity}`}
                                                >
                                                    {row.coverageDensity}
                                                </span>
                                            )}
                                            <span className={`manabaseColorStatus ${statusClass}`}>
                                                {statusLabel}
                                            </span>
                                        </div>
                                        <div className="manabaseColorBars">
                                            <div className="manabaseColorBarRow">
                                                <span className="manabaseColorBarLabel">Req</span>
                                                <div className="manabaseColorBarTrack">
                                                    <div
                                                        className="manabaseColorBarFill manabaseColorBarFill--req"
                                                        style={{ width: `${(row.avgReq / max) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="manabaseColorBarValue">{row.avgReq}%</span>
                                            </div>
                                            <div className="manabaseColorBarRow">
                                                <span className="manabaseColorBarLabel">Prod</span>
                                                <div className="manabaseColorBarTrack">
                                                    <div
                                                        className="manabaseColorBarFill manabaseColorBarFill--prod"
                                                        style={{
                                                            width: `${(row.avgProd / max) * 100}%`,
                                                            background: row.meta.fill,
                                                        }}
                                                    />
                                                </div>
                                                <span className="manabaseColorBarValue">{row.avgProd}%</span>
                                            </div>
                                        </div>
                                        <ColorStatsPanel row={row} />
                                    </div>
                                );
                            })}
                        </div>
                    </StatsSection>
                )}

                {/* ===== TAP-LAND DETAILS ===== */}
                {tapLandRows.length > 0 && (
                    <StatsSection
                        title="Tap lands penalized"
                        subtitle={`${tapLandRows.length} enters-tapped land${
                            tapLandRows.length === 1 ? '' : 's'
                        } the scorer docked speed for`}
                        helpHeading="Tap lands"
                        helpContent={
                            <Flex direction="column" rowGap={6}>
                                <Text>
                                    These lands enter tapped and reduce your deck's speed score. The effective penalty
                                    is the raw penalty scaled by two modifiers: <b>effective = raw × relief × speed</b>.
                                </Text>
                                <Text>
                                    Swapping these for untapped duals, shocks, or fetches is the most direct way to push
                                    the quality score up.
                                </Text>
                            </Flex>
                        }
                    >
                        <div className="manabaseTapLandMetrics">
                            <div className="manabaseTapLandMetric">
                                <span className="manabaseTapLandMetricLabel">Raw penalty</span>
                                <span className="manabaseTapLandMetricValue">
                                    −{formatNumber(debugManabase?.tapLandPenalty)}
                                </span>
                            </div>
                            <div className="manabaseTapLandMetric">
                                <span className="manabaseTapLandMetricLabel">Effective</span>
                                <span className="manabaseTapLandMetricValue">
                                    −{formatNumber(debugManabase?.effectiveTapLandPenalty)}
                                </span>
                            </div>
                            {Number.isFinite(parseFloat(debugManabase?.tapLandReliefFactor)) && (
                                <div className="manabaseTapLandMetric">
                                    <span className="manabaseTapLandMetricLabel">Relief factor</span>
                                    <span className="manabaseTapLandMetricValue">
                                        ×{formatNumber(debugManabase?.tapLandReliefFactor)}
                                    </span>
                                </div>
                            )}
                            {Number.isFinite(parseFloat(debugManabase?.tapLandSpeedMultiplier)) && (
                                <div className="manabaseTapLandMetric">
                                    <span className="manabaseTapLandMetricLabel">Speed multiplier</span>
                                    <span className="manabaseTapLandMetricValue">
                                        ×{formatNumber(debugManabase?.tapLandSpeedMultiplier)}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="manabaseTapLandList">
                            {tapLandRows.map((row) => (
                                <div key={row.id || row.name} className="manabaseTapLandRow">
                                    <span className="manabaseTapLandName">{row.name}</span>
                                    {row.raw?.metadataType && (
                                        <span className="manabaseTapLandType">{row.raw.metadataType}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </StatsSection>
                )}
            </Flex>
        </BaseDeckPane>
    );
}

/**
 * Column in the manabase profile grid — one of
 * Strengths / Risks / Suggestions. Each item is already pre-rendered
 * (`{ id, text }`) by `renderProfileItems`; this component only arranges
 * the list and handles the empty state.
 */
function ProfileMarkerColumn({ tone, title, subtitle, items, emptyText }) {
    return (
        <div className={`manabaseProfileColumn manabaseProfileColumn--${tone}`}>
            <div className="manabaseProfileColumnHeader">
                <span className="manabaseProfileColumnArrow" aria-hidden="true">
                    {tone === 'up' ? '▲' : tone === 'down' ? '▼' : '↑'}
                </span>
                <span className="manabaseProfileColumnTitle">{title}</span>
                <span className="manabaseProfileColumnCount">{items.length}</span>
            </div>
            <span className="manabaseProfileColumnSubtitle">{subtitle}</span>
            {items.length === 0 ? (
                <span className="manabaseProfileColumnEmpty">{emptyText}</span>
            ) : (
                <ul className="manabaseProfileList">
                    {items.map((item) => (
                        <li key={item.id} className="manabaseProfileListItem">
                            {item.text}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/**
 * Tempo laggard table — one row per card whose earliest cast slips
 * past its own mana value. Rendered as a compact list so a deck with
 * dozens of laggards doesn't dominate the pane; the caller slices the
 * top 12 before passing `rows`.
 */
function TempoLaggardList({ rows }) {
    return (
        <div className="manabaseTempoTable">
            <div className="manabaseTempoHeader">
                <span className="manabaseTempoCellName">Card</span>
                <span className="manabaseTempoCellNum">MV</span>
                <span className="manabaseTempoCellNum">Earliest</span>
                <span className="manabaseTempoCellNum">On-curve</span>
                <span className="manabaseTempoCellNum">Slip</span>
                <span className="manabaseTempoCellNum">Eff</span>
            </div>
            {rows.map((row) => (
                <div key={row.id} className="manabaseTempoRow">
                    <span className="manabaseTempoCellName" title={row.name}>
                        {row.name}
                    </span>
                    <span className="manabaseTempoCellNum">{row.cmc}</span>
                    <span className="manabaseTempoCellNum">T{row.earliest}</span>
                    <span className="manabaseTempoCellNum">T{row.onCurve}</span>
                    <span className={`manabaseTempoCellNum ${row.slip > 0 ? 'is-deficit' : ''}`}>
                        {row.slip > 0 ? `+${row.slip}` : row.slip}
                    </span>
                    <span
                        className={`manabaseTempoCellNum ${
                            row.efficiency != null && row.efficiency < 1 ? 'is-deficit' : ''
                        }`}
                    >
                        {row.efficiency != null ? formatNumber(row.efficiency) : '—'}
                    </span>
                </div>
            ))}
        </div>
    );
}

/**
 * Single highlight group — labeled bucket of producer chips (fast
 * mana, any-color fixers, etc.). Returns null when the bucket is
 * empty so the parent doesn't have to guard each group individually.
 */
function ProducerChipGroup({ label, entries, cards }) {
    if (!entries || entries.length === 0) return null;
    const totalQty = entries.reduce((sum, e) => sum + (e.qty || 1), 0);
    return (
        <div className="manabaseHighlightGroup">
            <div className="manabaseHighlightHeader">
                <span className="manabaseHighlightLabel">{label}</span>
                <span className="manabaseHighlightCount">{totalQty}</span>
            </div>
            <div className="manabaseHighlightChipRow">
                {entries.map((entry) => {
                    const name = cards?.[entry.containerId]?.name || entry.containerId;
                    const qty = entry.qty > 1 ? ` ×${entry.qty}` : '';
                    return (
                        <span key={entry.containerId} className="manabaseHighlightChip" title={name}>
                            <span className="manabaseHighlightChipName">{name}</span>
                            {qty && <span className="manabaseHighlightChipQty">{qty}</span>}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Deep-dive stats panel for a single color — compact list of all the
 * sub-counters the deck API returns (pip quantity, density, fetches,
 * ramp, etc.). Renders inside the color row so it's discoverable but
 * doesn't dominate the visual weight.
 */
function ColorStatsPanel({ row }) {
    const d = row?.details || {};
    const ratioDisplay = d.productionToRequirementRatio != null ? formatNumber(d.productionToRequirementRatio) : null;
    const entries = [
        { label: 'Prod / req', value: ratioDisplay },
        { label: 'Pip quantity', value: d.requirementsCount },
        { label: 'Avg density', value: d.averageDensity },
        { label: 'Total produced', value: d.totalProduced },
        { label: 'Avg produced', value: d.averageProduced },
        { label: 'Commander pips', value: d.commanderPipCount },
        {
            label: 'Commander %',
            value: d.commanderPipRatio != null ? `${Math.round(d.commanderPipRatio * 100)}%` : null,
        },
        { label: 'Slow producers', value: d.slowQuantity },
        { label: 'Basics', value: d.basicLands },
        { label: 'Fetchable', value: d.fetchableLands },
        { label: 'Fetches', value: d.fetchesCount },
        { label: 'Basic fetches', value: d.basicFetchesCount },
        { label: 'Land ramp', value: d.landRampCount },
    ].filter((e) => e.value != null && e.value !== '' && e.value !== 0);

    const producerTypes = Object.entries(d.producerTypes || {}).filter(([, v]) => v);

    if (entries.length === 0 && producerTypes.length === 0) return null;

    return (
        <div className="manabaseColorDetailGrid">
            {entries.map((e) => (
                <div key={e.label} className="manabaseColorDetailCell">
                    <span className="manabaseColorDetailLabel">{e.label}</span>
                    <span className="manabaseColorDetailValue">{e.value}</span>
                </div>
            ))}
            {producerTypes.map(([type, v]) => (
                <div key={`type_${type}`} className="manabaseColorDetailCell">
                    <span className="manabaseColorDetailLabel">
                        {type.charAt(0).toUpperCase()}
                        {type.slice(1)}
                    </span>
                    <span className="manabaseColorDetailValue">{v}</span>
                </div>
            ))}
        </div>
    );
}
